import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localizedFormat from "dayjs/plugin/localizedFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import {
  DeployedPlanDayRead,
  DeployedPlanDaysRead,
  DeployedPlanRead,
} from "src/store/api/generatedApi";
import { toggleMember } from "helpers";
import { z } from "zod";

dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

export const viewerTimezone = (): string => {
  const guessedTimezone = dayjs.tz.guess();
  return guessedTimezone && guessedTimezone !== "Etc/Unknown"
    ? guessedTimezone
    : "UTC";
};

export interface DeployedPlanMatch {
  matchText: string | undefined;
  matchDate: string | undefined;
}

export const parseMatchInput = (text: string): DeployedPlanMatch => {
  const matchText = text.trim();
  if (matchText === "") {
    return { matchText: undefined, matchDate: undefined };
  }
  const parsedDate = dayjs(matchText, "L", true);
  return {
    matchText,
    matchDate: parsedDate.isValid()
      ? parsedDate.format("YYYY-MM-DD")
      : undefined,
  };
};

const DeployedPlanDaysCounts = z.object({
  items: z.array(z.unknown()),
  total: z.int(),
  plan_total: z.int(),
});

export const hasDayCounts = (response: DeployedPlanDaysRead): boolean =>
  DeployedPlanDaysCounts.safeParse(response).success;

export interface DeployedPlanDaysRequest {
  committedText: string;
  page: number;
  pageSize: number;
}

export type DeployedPlanDaysRequestAction =
  | { type: "commitText"; text: string }
  | { type: "setPagination"; page: number; pageSize: number };

export const deployedPlanDaysRequestReducer = (
  state: DeployedPlanDaysRequest,
  action: DeployedPlanDaysRequestAction
): DeployedPlanDaysRequest => {
  switch (action.type) {
    case "commitText":
      return action.text === state.committedText
        ? state
        : { ...state, committedText: action.text, page: 0 };
    case "setPagination":
      return action.page === state.page && action.pageSize === state.pageSize
        ? state
        : { ...state, page: action.page, pageSize: action.pageSize };
  }
};

export interface OpenDays {
  defaultOpen: "none" | "all";
  toggled: ReadonlySet<string>;
}

export type OpenDaysAction =
  | { type: "toggle"; deployedOn: string }
  | { type: "expandAll" }
  | { type: "collapseAll" };

export const openDaysReducer = (
  state: OpenDays,
  action: OpenDaysAction
): OpenDays => {
  switch (action.type) {
    case "toggle":
      return {
        ...state,
        toggled: toggleMember(state.toggled, action.deployedOn),
      };
    case "expandAll":
      return { defaultOpen: "all", toggled: new Set() };
    case "collapseAll":
      return { defaultOpen: "none", toggled: new Set() };
  }
};

export const isDayOpen = (openDays: OpenDays, deployedOn: string): boolean =>
  (openDays.defaultOpen === "all") !== openDays.toggled.has(deployedOn);

export interface DeployedPlanDaySummary {
  deployedOn: string;
  planCount: number;
  listedPlanCount: number;
  isOpen: boolean;
}

export type DeployedPlanGridRow = DeployedPlanRead & {
  day: DeployedPlanDaySummary | null;
  isLastInDay: boolean;
};

export const flattenDeployedPlanDays = ({
  days,
  openDays,
}: {
  days: DeployedPlanDayRead[];
  openDays: OpenDays;
}): DeployedPlanGridRow[] =>
  days.flatMap(({ deployed_on, plan_count, plans }) => {
    const [dayPlan, ...earlierPlans] = plans;
    if (dayPlan === undefined) {
      return [];
    }
    const isOpen = earlierPlans.length > 0 && isDayOpen(openDays, deployed_on);
    const dayRow: DeployedPlanGridRow = {
      ...dayPlan,
      day: {
        deployedOn: deployed_on,
        planCount: plan_count,
        listedPlanCount: plans.length,
        isOpen,
      },
      isLastInDay: false,
    };
    const earlierRows = isOpen
      ? earlierPlans.map((plan, index): DeployedPlanGridRow => ({
          ...plan,
          day: null,
          isLastInDay: index === earlierPlans.length - 1,
        }))
      : [];
    return [dayRow, ...earlierRows];
  });
